// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Heart,
  Sparkles,
  Calendar,
  Users,
  ChevronRight,
  UserCircle,
  Megaphone,
  UserPlus,
  Check,
  X,
} from 'lucide-react';
import userService from '../services/userService';
import eventService from '../services/eventService';
import annonceService from '../services/annonceService';
import friendService from '../services/friendService';
import {
  profileCompletion,
  getProfileMissingItems,
  getDisplayAge,
  canAccessDating,
  calculateAge,
  formatDateShort,
  timeAgo,
  formatPrice,
} from '../utils';
import { getCategoryLabel, CATEGORY_COLORS, getAnnonceCategoryLabel } from '../constants';
import { Btn, Card, Badge, Loading, Empty, ErrorState } from '../components/ui';
import ActivateDatingModal from '../modals/ActivateDatingModal';

const Dashboard = ({ user, onUpdate, toast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [recentAnnonces, setRecentAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showActivateDating, setShowActivateDating] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friendActionLoading, setFriendActionLoading] = useState(null);
  const [completionDismissed, setCompletionDismissed] = useState(() => {
    try {
      const ts = localStorage.getItem('hmh-completion-dismissed');
      return ts && Date.now() - Number(ts) < 86400000;
    } catch {
      return false;
    }
  });
  const isDating = canAccessDating(user);
  const userAge = calculateAge(user.dateOfBirth);

  const load = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);

      // Charger les demandes d'amis en parallèle
      friendService.getPendingRequestsReceived(user.uid).then((res) => {
        if (res.success) setFriendRequests(res.data);
      });

      const promises = [eventService.getUpcomingEvents(3)];

      if (isDating) {
        promises.push(userService.getProfiles(user), userService.getMatches(user.uid));
      }

      promises.push(eventService.getUserEvents(user.uid));
      promises.push(annonceService.getAnnonces());

      const results = await Promise.all(promises);

      setUpcomingEvents(results[0]);

      if (isDating) {
        setProfiles(results[1].slice(0, 3));
        setMatches(results[2]);
        const userEvts = results[3];
        const now = Date.now();
        setMyEvents(
          userEvts
            .filter((ev) => {
              const d = ev.date?.seconds ? ev.date.seconds * 1000 : new Date(ev.date).getTime();
              return d > now && ev.status === 'active';
            })
            .slice(0, 2)
        );
      } else {
        const userEvts = results[1];
        const now = Date.now();
        setMyEvents(
          userEvts
            .filter((ev) => {
              const d = ev.date?.seconds ? ev.date.seconds * 1000 : new Date(ev.date).getTime();
              return d > now && ev.status === 'active';
            })
            .slice(0, 2)
        );
      }

      // Annonces : toujours le dernier résultat
      const annoncesResult = results[results.length - 1];
      if (annoncesResult.success) setRecentAnnonces(annoncesResult.data.slice(0, 3));
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, isDating]);

  useEffect(() => {
    load();
  }, [load]);

  const pct = profileCompletion(user);
  const missingItems = getProfileMissingItems(user);
  const userMode = user.mode || 'community';

  const handleAcceptFriend = async (requestId, fromUid) => {
    setFriendActionLoading(fromUid);
    const result = await friendService.acceptFriendRequest(requestId);
    if (result.success) {
      setFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast(t('friends.request.accepted'), 'success');
    } else {
      toast(result.error || t('friends.errors.generic'), 'error');
    }
    setFriendActionLoading(null);
  };

  const handleDeclineFriend = async (requestId, fromUid) => {
    setFriendActionLoading(fromUid);
    const result = await friendService.declineFriendRequest(requestId);
    if (result.success) {
      setFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast(t('friends.request.declined'), 'success');
    } else {
      toast(result.error || t('friends.errors.generic'), 'error');
    }
    setFriendActionLoading(null);
  };

  const dismissCompletion = () => {
    setCompletionDismissed(true);
    try {
      localStorage.setItem('hmh-completion-dismissed', String(Date.now()));
    } catch {
      // localStorage non disponible
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <ErrorState onRetry={load} />
      </div>
    );
  }

  return (
    <div className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4 overflow-hidden">
      <Helmet>
        <title>{t('dashboard.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/" />
      </Helmet>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-2xl font-bold mb-1 dark:text-gray-100">
          {t('dashboard.greeting', { name: user.name })}
        </h1>
        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className={`grid gap-3 mb-4 sm:mb-6 grid-cols-2 sm:grid-cols-3`}>
        {[
          {
            l: t('dashboard.stats.profile'),
            v: `${pct}%`,
            i: User,
            c: 'rose',
            onClick: () => navigate('/profile'),
          },
          ...(isDating
            ? [
                {
                  l: t('dashboard.stats.likesSent'),
                  v: user.likes?.length || 0,
                  i: Heart,
                  c: 'pink',
                  onClick: () => navigate('/matches'),
                },
                {
                  l: t('dashboard.stats.matches'),
                  v: user.matches?.length || 0,
                  i: Sparkles,
                  c: 'purple',
                  onClick: () => navigate('/matches'),
                },
              ]
            : [
                {
                  l: t('dashboard.stats.friends'),
                  v: user.friends?.length || 0,
                  i: Users,
                  c: 'purple',
                  onClick: () => navigate('/members'),
                },
                {
                  l: t('dashboard.stats.eventsJoined'),
                  v: myEvents.length,
                  i: Calendar,
                  c: 'pink',
                  onClick: () => navigate('/events'),
                },
              ]),
        ].map((s, i) => (
          <Card key={i} className="p-3 sm:p-4 cursor-pointer" hover onClick={s.onClick}>
            <div
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2 ${
                s.c === 'rose'
                  ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                  : s.c === 'pink'
                    ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
              }`}
            >
              <s.i size={18} />
            </div>
            <p className="text-lg sm:text-xl font-bold dark:text-gray-100">{s.v}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.l}</p>
          </Card>
        ))}
      </div>

      {/* Demandes d'amis en attente */}
      {friendRequests.length > 0 && (
        <Card className="p-4 mb-6" data-testid="dashboard-friend-requests">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={18} className="text-violet-500" />
            <h2 className="font-bold text-sm dark:text-gray-100">
              {t('dashboard.friendRequests.title')}
            </h2>
            <span className="bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-bold rounded-full px-2 py-0.5">
              {friendRequests.length}
            </span>
            {friendRequests.length > 5 && (
              <Btn v="ghost" size="sm" onClick={() => navigate('/members')} className="ml-auto">
                {t('dashboard.friendRequests.seeAll')}
              </Btn>
            )}
          </div>
          <div className="space-y-2">
            {friendRequests.slice(0, 5).map((req) => (
              <div
                key={req.id}
                data-testid="dashboard-friend-request-card"
                className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-xl p-3"
              >
                {req.fromPhoto ? (
                  <img
                    src={req.fromPhoto}
                    alt={req.fromName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <UserCircle
                    size={40}
                    className="text-gray-300 dark:text-gray-600 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm dark:text-gray-200 truncate">
                    {req.fromName}
                  </p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleAcceptFriend(req.id, req.fromUid)}
                    disabled={friendActionLoading === req.fromUid}
                    data-testid="dashboard-friend-accept"
                    className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition"
                    aria-label={t('members.accept')}
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => handleDeclineFriend(req.id, req.fromUid)}
                    disabled={friendActionLoading === req.fromUid}
                    data-testid="dashboard-friend-decline"
                    className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                    aria-label={t('members.decline')}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bandeau complétion intelligent */}
      {pct < 100 && !completionDismissed && (
        <Card
          data-testid="dashboard-completion-banner"
          className={`p-4 mb-6 ${
            userMode === 'dating' || userMode === 'both'
              ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800'
              : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <p className="font-bold text-sm dark:text-gray-100 mb-1">
                {userMode === 'dating' || userMode === 'both'
                  ? t('dashboard.completion.dating', { pct })
                  : t('dashboard.completion.community')}
              </p>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    userMode === 'dating' || userMode === 'both'
                      ? 'bg-gradient-to-r from-rose-500 to-fuchsia-500'
                      : 'bg-gradient-to-r from-violet-500 to-purple-500'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <button
              onClick={dismissCompletion}
              data-testid="dashboard-completion-dismiss"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {missingItems.map((item) => (
              <button
                key={item}
                onClick={() => navigate('/profile')}
                className="px-2.5 py-1 text-xs font-medium rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-violet-400 dark:hover:border-violet-500 hover:text-violet-600 dark:hover:text-violet-400 transition"
              >
                {t(item)}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Matches récents */}
      {isDating && matches.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-purple-600 dark:text-purple-400" />
              <h2 className="text-sm sm:text-lg font-semibold dark:text-gray-100">
                {t('dashboard.yourMatches')}
              </h2>
            </div>
            <Btn v="ghost" onClick={() => navigate('/matches')}>
              {t('common.viewAll')}
              <ChevronRight size={16} />
            </Btn>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {matches.slice(0, 6).map((m) => (
              <div
                key={m.uid}
                className="flex-shrink-0 cursor-pointer group"
                onClick={() => navigate('/matches')}
              >
                <div className="relative">
                  {m.profilePicture ? (
                    <img
                      src={m.profilePicture}
                      alt={m.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-purple-300 group-hover:border-purple-500 transition"
                    />
                  ) : (
                    <UserCircle
                      size={64}
                      className="text-gray-400 border-2 border-purple-300 rounded-full"
                    />
                  )}
                  {m.online && (
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
                  )}
                </div>
                <p className="text-xs text-center mt-1 font-medium text-gray-700 dark:text-gray-300 max-w-[64px] truncate">
                  {m.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mes prochaines participations */}
      {myEvents.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar size={20} className="text-rose-500 flex-shrink-0" />
              <h2 className="text-sm sm:text-lg font-semibold dark:text-gray-100 truncate">
                {t('dashboard.upcomingParticipations')}
              </h2>
            </div>
            <Btn v="ghost" onClick={() => navigate('/events')} className="flex-shrink-0">
              {t('common.viewAll')}
              <ChevronRight size={16} />
            </Btn>
          </div>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 sm:overflow-visible">
            {myEvents.map((ev) => (
              <Card
                key={ev.id}
                hover
                onClick={() => navigate(`/events/${ev.id}`)}
                className="p-3 flex gap-3 items-center min-w-[260px] max-w-[300px] flex-shrink-0 snap-start sm:min-w-0 sm:max-w-none"
              >
                <div className="w-12 h-12 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center flex-shrink-0">
                  <Calendar size={20} className="text-rose-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm dark:text-gray-200 truncate">{ev.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {formatDateShort(ev.date)}
                    {ev.online
                      ? ` · ${t('common.online')}`
                      : ev.location?.city
                        ? ` · ${ev.location.city}`
                        : ''}
                  </p>
                </div>
                <Badge c="green">{t('common.registered')}</Badge>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Événements à venir */}
      <Card className="p-4 sm:p-5 mb-4 sm:mb-6 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800">
        <div className="flex justify-between items-center mb-3 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Calendar size={20} className="text-violet-600 dark:text-violet-400 flex-shrink-0" />
            <h2 className="text-sm sm:text-lg font-semibold text-violet-900 dark:text-violet-300 truncate">
              {t('dashboard.upcomingEvents')}
            </h2>
          </div>
          <Btn
            v="ghost"
            onClick={() => navigate('/events')}
            className="text-violet-600 dark:text-violet-400 flex-shrink-0"
          >
            {t('common.viewAll')}
            <ChevronRight size={16} />
          </Btn>
        </div>
        {upcomingEvents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            {t('dashboard.noUpcomingEvents')}{' '}
            <button
              onClick={() => navigate('/events')}
              className="text-violet-600 dark:text-violet-400 hover:underline font-medium"
            >
              {t('dashboard.seeEvents')}
            </button>
          </p>
        ) : (
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 sm:overflow-visible">
            {upcomingEvents.map((ev) => (
              <Card
                key={ev.id}
                hover
                onClick={() => navigate(`/events/${ev.id}`)}
                className="p-3 flex gap-3 items-center min-w-[260px] max-w-[300px] flex-shrink-0 snap-start sm:min-w-0 sm:max-w-none"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    CATEGORY_COLORS[ev.category] === 'green'
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500'
                      : CATEGORY_COLORS[ev.category] === 'purple'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-500'
                        : CATEGORY_COLORS[ev.category] === 'blue'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
                          : 'bg-violet-100 dark:bg-violet-900/30 text-violet-500'
                  }`}
                >
                  <Calendar size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm dark:text-gray-200 truncate">{ev.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {formatDateShort(ev.date)}
                    {ev.online
                      ? ` · ${t('common.online')}`
                      : ev.location?.city
                        ? ` · ${ev.location.city}`
                        : ''}
                  </p>
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {t(getCategoryLabel(ev.category))}
                </span>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Dernières annonces */}
      {recentAnnonces.length > 0 && (
        <div className="mb-6" data-testid="dashboard-annonces-section">
          <div className="flex justify-between items-center mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Megaphone size={20} className="text-rose-500 flex-shrink-0" />
              <h2 className="text-sm sm:text-lg font-semibold dark:text-gray-100 truncate">
                {t('dashboard.recentAnnonces')}
              </h2>
            </div>
            <Btn v="ghost" onClick={() => navigate('/annonces')} className="flex-shrink-0">
              {t('common.viewAll')}
              <ChevronRight size={16} />
            </Btn>
          </div>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible">
            {recentAnnonces.map((a) => (
              <Card
                key={a.id}
                hover
                onClick={() => navigate(`/annonces/${a.id}`)}
                className="p-3 min-w-[260px] max-w-[300px] flex-shrink-0 snap-start sm:min-w-0 sm:max-w-none"
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="font-semibold text-sm dark:text-gray-200 truncate flex-1 mr-2">
                    {a.title}
                  </p>
                  <Badge c="rose">{t(getAnnonceCategoryLabel(a.category))}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span className="truncate">
                    {a.city}
                    {a.price ? ` · ${formatPrice(a.price)}` : ''}
                  </span>
                  <span className="flex-shrink-0 ml-2">{timeAgo(a.createdAt)}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* CTA Activer les Rencontres (adultes sans dating activé) */}
      {!isDating && userAge !== null && userAge >= 18 && (
        <Card className="p-5 mb-6 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800">
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
              <Heart size={22} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-rose-900 dark:text-rose-400 mb-1">
                {userMode === 'dating' || userMode === 'both'
                  ? t('dashboard.datingCta.titleHasDating')
                  : t('dashboard.datingCta.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {userMode === 'dating' || userMode === 'both'
                  ? t('dashboard.datingCta.descHasDating')
                  : t('dashboard.datingCta.desc')}
              </p>
              <Btn onClick={() => setShowActivateDating(true)}>
                {userMode === 'dating' || userMode === 'both'
                  ? t('dashboard.datingCta.buttonHasDating')
                  : t('dashboard.datingCta.button')}
              </Btn>
            </div>
          </div>
        </Card>
      )}

      {/* Suggestions de profils (dating only) */}
      {isDating && (
        <>
          <div className="flex justify-between mb-4">
            <h2 className="text-sm sm:text-lg font-semibold dark:text-gray-100">
              {t('dashboard.suggestions.title')}
            </h2>
            <Btn v="ghost" onClick={() => navigate('/discover')}>
              {t('common.viewAll')}
              <ChevronRight size={16} />
            </Btn>
          </div>
          {profiles.length === 0 ? (
            <Empty
              icon={Users}
              title={t('dashboard.suggestions.emptyTitle')}
              desc={t('dashboard.suggestions.emptyDesc')}
              action={
                <Btn v="outline" onClick={() => navigate('/profile')}>
                  {t('dashboard.completeProfile.cta')}
                </Btn>
              }
            />
          ) : (
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible">
              {profiles.map((p, index) => (
                <Card
                  key={p.uid}
                  className="overflow-hidden animate-fade-in-up min-w-[260px] max-w-[300px] flex-shrink-0 snap-start sm:min-w-0 sm:max-w-none"
                  style={{ animationDelay: `${index * 60}ms` }}
                  hover
                  onClick={() => navigate('/discover')}
                >
                  <div className="h-44 relative">
                    {p.profilePicture ? (
                      <img
                        src={p.profilePicture}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <UserCircle size={48} className="text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-3 left-3 text-white">
                      <span className="font-bold">
                        {p.name}
                        {getDisplayAge(p) !== null ? `, ${getDisplayAge(p)}` : ''}
                      </span>
                      <br />
                      <span className="text-xs text-white/80">
                        {p.clan || t('common.hmongCommunity')}
                        {p.city ? ` · ${p.city}` : ''}
                      </span>
                    </div>
                    {p.online && (
                      <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <ActivateDatingModal
        isOpen={showActivateDating}
        onClose={() => setShowActivateDating(false)}
        user={user}
        onUpdate={onUpdate}
        toast={toast}
      />
    </div>
  );
};

export default Dashboard;
